import type { Middleware } from 'openapi-fetch';
import type { AuthStoreAdapter, AuthFailureDetails } from '@billda/shared-ui/src/api/types/auth';
import { isTokenWithinExpiryBuffer } from '@billda/shared-ui/src/api/token/expiry';

/** Options for the client factory; the auth-focused subset of createAxiosClient's options. */
export interface CreateBilldaClientOptions {
  /** API base URL, e.g. 'http://localhost:6550'. */
  baseUrl: string;
  /** Portal Gateway base URL. Defaults to baseUrl. */
  portalGatewayBaseUrl?: string;
  /** Mobile Gateway base URL. Defaults to baseUrl. */
  mobileGatewayBaseUrl?: string;
  /** Adapter over the app's auth store (getToken/getRefreshToken/setTokens/logout). */
  authStore: AuthStoreAdapter;
  /** Called when a token refresh fails with an auth error (drives logout). */
  onAuthFailure: (details: AuthFailureDetails) => void;
  /** Sent as X-Client-Type on every request. */
  clientType: 'portal' | 'mobile';
  /** Sent as X-Client-Version on every request. */
  appVersion: string;
  /** Optional per-request connection type, sent as X-Client-Connection-Type (mobile). */
  getConnectionType?: () => string;
  /** Per-request timeout in milliseconds. See DEFAULT_TIMEOUT_MS in client.ts. */
  timeoutMs?: number;
  /**
   * Optional shared refresh delegate. When provided, every refresh path
   * (proactive, 401, 403) calls this instead of the middleware's own refresh
   * POST, so the middleware and the app's axios stack share one refresh and
   * can't rotate the refresh token out from under each other. Returns the new
   * access token, or null if the refresh failed — in the null case the shared
   * owner is responsible for reporting the auth failure / logout, so the
   * middleware surfaces the original response without calling onAuthFailure.
   */
  sharedRefresh?: () => Promise<string | null>;
}

/** Proactively refresh when the access token is within this many seconds of expiry. */
const REFRESH_BUFFER_SECONDS = 10;
const REFRESH_PATH = '/api/v1/auth/refresh';
/** Delays between the 3 refresh attempts (1 initial + 2 retries), network errors only. */
const REFRESH_RETRY_DELAYS_MS = [500, 1000];

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/** Auth-failure classification carried out of a failed refresh. */
interface RefreshFailureInfo {
  /** Whether onAuthFailure should fire (drives logout). */
  authFailure: boolean;
  /** No refresh token was available. */
  noRefreshToken?: boolean;
  /** The refresh POST itself was rejected (400/401/403 or invalid body). */
  refreshFailed?: boolean;
  /** Refresh exhausted its retries on network errors — should NOT log out. */
  networkError?: boolean;
}

/**
 * Refresh failure that carries the failure kind so onAuthFailure receives
 * accurate flags. A network exhaustion (no HTTP response after retries) keeps
 * the user logged in; a genuine auth failure (expired/invalid refresh token →
 * 400/401/403) logs out.
 */
class RefreshFailure extends Error {
  info: RefreshFailureInfo;
  constructor(message: string, info: RefreshFailureInfo) {
    super(message);
    this.name = 'RefreshFailure';
    this.info = info;
  }
}

/**
 * Build the shared auth middleware for a Billda openapi-fetch client.
 *
 * onRequest: proactively refresh a near-expiry token, attach the Bearer token,
 * the X-Client-* headers, and stash a body clone so a replay after refresh can
 * re-send the (already-consumed) request body.
 * onResponse: on 401/403 refresh once (deduped so concurrent failures trigger a
 * single refresh) and replay the request once with the new token; on auth-failure
 * refresh, invoke onAuthFailure and surface the original response.
 */
export function createAuthMiddleware(options: CreateBilldaClientOptions): Middleware {
  const { baseUrl, authStore, onAuthFailure, clientType, appVersion, getConnectionType, sharedRefresh } =
    options;

  // The first send consumes the Request body, so a replay needs its own copy.
  // Keyed by the exact Request instance onRequest returns (same reference passed
  // to onResponse), so the map self-cleans as requests are GC'd.
  const bodyClones = new WeakMap<Request, Request>();

  // Single in-flight refresh shared by onRequest and onResponse. Concurrent 401s
  // all await the same promise, so exactly one refresh runs.
  let refreshPromise: Promise<string> | null = null;

  async function performRefresh(): Promise<string> {
    if (sharedRefresh) {
      const token = await sharedRefresh();
      if (!token) {
        // The shared refresh owner (axios stack) already reported the failure
        // and drove logout; surface the original response, don't double-fire.
        throw new RefreshFailure('Shared token refresh failed', { authFailure: false });
      }
      return token;
    }

    const refreshToken = authStore.getRefreshToken();
    if (!refreshToken) {
      throw new RefreshFailure('No refresh token available', {
        authFailure: true,
        noRefreshToken: true,
      });
    }

    const maxAttempts = REFRESH_RETRY_DELAYS_MS.length + 1;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      let response: Response;
      try {
        response = await fetch(`${baseUrl}${REFRESH_PATH}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Client-Type': clientType,
            'X-Client-Version': appVersion,
          },
          body: JSON.stringify({ refreshToken }),
        });
      } catch (error) {
        // No HTTP response: transient network error. Retry with a delay if
        // attempts remain, otherwise surface without logging the user out.
        if (attempt < maxAttempts) {
          await sleep(REFRESH_RETRY_DELAYS_MS[attempt - 1]);
          continue;
        }
        throw new RefreshFailure(
          error instanceof Error ? error.message : 'Token refresh network error',
          { authFailure: false, networkError: true }
        );
      }

      if (!response.ok) {
        // An HTTP response reached us: this is not a network error, so no retry.
        const isAuthFailure =
          response.status === 400 || response.status === 401 || response.status === 403;
        throw new RefreshFailure(`Token refresh failed (${response.status})`, {
          authFailure: isAuthFailure,
          refreshFailed: isAuthFailure,
        });
      }

      const data = (await response.json()) as {
        identityToken?: string;
        refreshToken?: string;
      };
      if (!data.identityToken || !data.refreshToken) {
        throw new RefreshFailure('Invalid token refresh response', {
          authFailure: true,
          refreshFailed: true,
        });
      }

      authStore.setTokens(data.identityToken, data.refreshToken);
      return data.identityToken;
    }

    // Unreachable: the loop returns or throws on every path.
    throw new RefreshFailure('Token refresh failed', { authFailure: false, networkError: true });
  }

  function refresh(): Promise<string> {
    if (!refreshPromise) {
      refreshPromise = performRefresh().finally(() => {
        refreshPromise = null;
      });
    }
    return refreshPromise;
  }

  function reportAuthFailure(message: string, info: RefreshFailureInfo): void {
    onAuthFailure({
      error: message,
      noRefreshToken: info.noRefreshToken,
      refreshFailed: info.refreshFailed,
      networkError: info.networkError ?? false,
      timestamp: new Date().toISOString(),
    });
  }

  return {
    async onRequest({ request }) {
      request.headers.set('X-Client-Type', clientType);
      request.headers.set('X-Client-Version', appVersion);

      if (getConnectionType) {
        const connectionType = getConnectionType();
        if (connectionType) {
          request.headers.set('X-Client-Connection-Type', connectionType);
        }
      }

      // Capture the body before the first send consumes it, so a post-refresh
      // replay can re-send the same bytes. GET/HEAD have no body.
      if (request.method !== 'GET' && request.method !== 'HEAD') {
        bodyClones.set(request, request.clone());
      }

      const token = authStore.getToken();
      if (!token) {
        return request;
      }

      const nearExpiry = isTokenWithinExpiryBuffer(token, REFRESH_BUFFER_SECONDS);

      if (nearExpiry && authStore.getRefreshToken()) {
        try {
          const newToken = await refresh();
          request.headers.set('Authorization', `Bearer ${newToken}`);
          return request;
        } catch {
          // Proactive refresh failed: continue with the existing token; the
          // response interceptor handles it if the token is truly rejected.
        }
      }

      request.headers.set('Authorization', `Bearer ${token}`);
      return request;
    },

    async onResponse({ request, response, options: reqOptions }) {
      if (response.status !== 401 && response.status !== 403) {
        return undefined;
      }

      let newToken: string;
      try {
        newToken = await refresh();
      } catch (error) {
        if (error instanceof RefreshFailure && error.info.authFailure) {
          reportAuthFailure(error.message, error.info);
        }
        return response;
      }

      request.headers.set('Authorization', `Bearer ${newToken}`);

      // Replay from the stored body clone so the consumed body is re-sent. Use
      // the client's fetch (timeout-wrapped) when available, else global fetch.
      const cloned = bodyClones.get(request);
      const replay = cloned
        ? new Request(cloned, { headers: request.headers })
        : new Request(request, { headers: request.headers });

      const replayFetch =
        (reqOptions as { fetch?: typeof fetch } | undefined)?.fetch ?? fetch;
      return replayFetch(replay);
    },
  };
}
